import { faExclamationCircle } from '@fortawesome/pro-regular-svg-icons';
import { useStripe } from '@stripe/react-stripe-js';
import { useQuery } from '@tanstack/react-query';
import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useTranslation } from 'react-i18next';
import { Alert } from 'src/components/Alert';
import { Avatar } from 'src/components/Avatar';
import { Box } from 'src/components/Box';
import { Button } from 'src/components/Button';
import { Card } from 'src/components/Card';
import { Chip } from 'src/components/Chip';
import { CircularProgress } from 'src/components/CircularProgress';
import { Divider } from 'src/components/Divider';
import { Icon } from 'src/components/Icon';
import { Link } from 'src/components/Link';
import { Stack } from 'src/components/Stack';
import { TextField } from 'src/components/TextField';
import { Typography } from 'src/components/Typography';
import { useNativeBridge } from 'src/contexts/NativeBridgeContext';
import { useSession } from 'src/contexts/SessionContext';
import { Cart } from 'src/models/cart/_types';
import {
  confirmCart,
  confirmTerminalPayment,
  setReaderDisplay,
} from 'src/models/cart/cart';
import { isTerminalPaymentMethod } from 'src/models/cart/utils';
import { PaymentMethod } from 'src/models/payment-methods/_types';
import {
  cancelTerminalAction,
  getTerminals,
} from 'src/models/terminal/terminal';
import { User } from 'src/models/user/_types';
import { getUserDisplayName, getUserInitials } from 'src/models/user/utils';
import { capitalizeFirstLetter } from 'src/utils/stringFormatter';

import { CartItem } from './CartItem';
import { PaymentStep, usePaymentDialogContext } from './PaymentDialogContext';

interface PaymentSummaryProps {
  cart: Cart;
  user?: User;
}

const PaymentSummary: React.FC<PaymentSummaryProps> = ({ cart, user }) => {
  const stripe = useStripe();
  const { session } = useSession();
  const hasSetDisplay = useRef(false);
  const [simulatedCardNumber, setSimulatedCardNumber] = useState('');
  const { data: terminals } = useQuery({
    queryKey: ['terminals'],
    queryFn: () => getTerminals({}),
  });
  const { t } = useTranslation('translation', { keyPrefix: 'client.payment' });
  const { openTapToPay } = useNativeBridge();

  const {
    setCurrentStep,
    savedCart,
    cartError,
    setCartError,
    fetchCartError,
    fetchCartIsFetching,
    fetchCartIsLoading,
  } = usePaymentDialogContext();

  const [confirmingCart, setConfirmingCart] = useState(false);

  useEffect(() => {
    const setupDisplay = async () => {
      if (
        !hasSetDisplay.current &&
        savedCart?.id &&
        typeof cart.paymentMethod === 'object' &&
        cart.paymentMethod?.type === 'card_present' &&
        cart.paymentMethod?.id
      ) {
        hasSetDisplay.current = true;
        try {
          await setReaderDisplay(savedCart.id, cart.paymentMethod.id);
        } catch (error: any) {
          // Non-critical error, can be ignored
        }
      }
    };

    setupDisplay();
  }, [savedCart?.id, cart.paymentMethod]);

  const handlePayment = useCallback(async () => {
    if (!stripe || !savedCart) return;

    setCartError(null);
    setConfirmingCart(true);

    try {
      if (savedCart?.paymentIntentSecret) {
        if (savedCart.tapToPay) {
          const result = await openTapToPay({
            paymentIntentSecret: savedCart.paymentIntentSecret,
            setupIntentSecret: savedCart.setupIntentSecret,
          });

          if (!result) {
            throw new Error('Tap to pay cancelled');
          }

          setConfirmingCart(false);
        } else if (
          typeof cart.paymentMethod === 'object' &&
          cart.paymentMethod?.paymentMethodType === 'bacs_debit'
        ) {
          const { error } = await confirmCart(savedCart.id);
          if (error) throw error;
        } else if (isTerminalPaymentMethod(cart)) {
          const terminalPaymentMethod = cart.paymentMethod as PaymentMethod;
          const response = await confirmTerminalPayment(
            savedCart.id,
            terminalPaymentMethod.id,
            false,
            simulatedCardNumber
          );
          if (response.error) throw new Error(response.error);
        } else {
          const { error } = await stripe.confirmPayment({
            clientSecret: savedCart.paymentIntentSecret,
            redirect: 'if_required',
            confirmParams: {
              payment_method:
                typeof cart.paymentMethod === 'string'
                  ? cart.paymentMethod
                  : session.destinationCharges
                  ? cart.paymentMethod?.processorId
                  : cart.paymentMethod?.subProcessorId,
            },
          });

          if (error) throw error;
        }
      } else if (savedCart?.setupIntentSecret) {
        if (savedCart.tapToPay) {
          const result = await openTapToPay({
            paymentIntentSecret: savedCart.paymentIntentSecret,
            setupIntentSecret: savedCart.setupIntentSecret,
          });

          if (!result) {
            throw new Error('Tap to pay cancelled');
          }

          setConfirmingCart(false);
        } else if (
          typeof cart.paymentMethod === 'object' &&
          cart.paymentMethod?.paymentMethodType === 'bacs_debit'
        ) {
          const { error } = await confirmCart(savedCart.id);
          if (error) throw error;
        } else if (isTerminalPaymentMethod(cart)) {
          const terminalPaymentMethod = cart.paymentMethod as PaymentMethod;
          const response = await confirmTerminalPayment(
            savedCart.id,
            terminalPaymentMethod.id,
            true,
            simulatedCardNumber
          );
          if (response.error) throw new Error(response.error);
        } else {
          const { error } = await stripe.confirmSetup({
            clientSecret: savedCart.setupIntentSecret,
            redirect: 'if_required',
            confirmParams: {
              payment_method:
                typeof cart.paymentMethod === 'string'
                  ? cart.paymentMethod
                  : session.destinationCharges
                  ? cart.paymentMethod?.processorId
                  : cart.paymentMethod?.subProcessorId,
            },
          });
          if (error) throw error;
        }
      } else {
        const { error } = await confirmCart(savedCart.id);
        if (error) throw error;
      }

      setCurrentStep(PaymentStep.PaymentComplete);
    } catch (error: any) {
      setCartError(error?.message || t('paymentError'));
      setConfirmingCart(false);
    }
  }, [
    cart,
    stripe,
    savedCart,
    setCartError,
    setCurrentStep,
    session.destinationCharges,
    simulatedCardNumber,
    t,
    openTapToPay,
  ]);

  const isSimulatedReader = useMemo(() => {
    if (
      typeof cart.paymentMethod === 'object' &&
      cart.paymentMethod?.type === 'card_present' &&
      cart.paymentMethod?.id
    ) {
      const reader = terminals?.find(
        (t) => t.stripeId === (cart.paymentMethod as any).id
      );
      return reader?.registrationCode?.includes('simulated') || false;
    }
    return false;
  }, [cart.paymentMethod, terminals]);

  if (fetchCartIsFetching || fetchCartIsLoading) {
    return (
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        minHeight={400}
      >
        <CircularProgress size="medium" />
      </Box>
    );
  }

  if (fetchCartError) {
    return (
      <Box p={3} textAlign="center">
        <Icon
          icon={faExclamationCircle}
          sx={{ fontSize: 48, color: 'error.main', mb: 2 }}
        />
        {t('loadingError', { error: cartError })}
      </Box>
    );
  }

  return (
    <Stack spacing={2} className="payment-summary">
      {cartError && <Alert severity="error">{cartError}</Alert>}

      {isSimulatedReader && (
        <TextField
          label="Simulated Card Number for Card Swipe"
          value={simulatedCardNumber}
          onChange={(e) => setSimulatedCardNumber(e.target.value)}
          placeholder="Enter test card number"
          fullWidth
          sx={{ mb: 2 }}
        />
      )}

      <Stack
        direction="row"
        sx={{
          alignItems: 'center',
          justifyContent: 'space-between',
          mt: 1,
          mb: 1,
        }}
      >
        <Stack direction="row" sx={{ alignItems: 'center' }}>
          <Avatar
            src={user?.profileImage}
            initials={user ? getUserInitials(user) : ''}
            sx={{ mr: 2 }}
          />
          <Typography variant="subtitle1" noWrap>
            {user ? getUserDisplayName(user) : 'Guest'}
          </Typography>
        </Stack>
        <Chip
          variant="outlined"
          color="primary"
          label={t('items', { count: cart.cartItems.length })}
        />
      </Stack>

      <Button
        variant="contained"
        onClick={handlePayment}
        color={'primary'}
        disabled={
          !savedCart ||
          !stripe ||
          fetchCartError ||
          fetchCartIsFetching ||
          fetchCartIsLoading ||
          confirmingCart
        }
        fullWidth
      >
        {savedCart?.paymentMethodTypeOrId === 'free' ? (
          <>{t('assignForFree')}</>
        ) : (
          <>
            {t('charge', {
              amount: new Intl.NumberFormat('en-US', {
                style: 'currency',
                currency: savedCart?.currency || 'usd',
              }).format(
                (savedCart?.priceBreakdown?.reduce(
                  (sum, item) => sum + item.total,
                  0
                ) || 0) / 100
              ),
            })}
          </>
        )}
      </Button>

      <Divider />

      {savedCart?.priceBreakdown && savedCart.priceBreakdown.length > 0 && (
        <Card>
          <Stack sx={{ p: 2 }} spacing={2}>
            <Stack spacing={1.5}>
              <Box
                display="flex"
                justifyContent="space-between"
                alignItems="center"
              >
                <Typography variant="body2" color="text.secondary">
                  {t('subtotal')}
                </Typography>
                <Typography
                  variant="body2"
                  color="text.secondary"
                  sx={{ textAlign: 'right', minWidth: '200px' }}
                >
                  {savedCart?.paymentMethodTypeOrId === 'free'
                    ? t('free')
                    : new Intl.NumberFormat('en-US', {
                        style: 'currency',
                        currency: savedCart?.currency || 'usd',
                      }).format(
                        savedCart.priceBreakdown.reduce(
                          (sum, item) => sum + item.price,
                          0
                        ) / 100
                      )}
                </Typography>
              </Box>

              {savedCart.priceBreakdown.some((item) => item.discount > 0) && (
                <Box
                  display="flex"
                  justifyContent="space-between"
                  alignItems="center"
                >
                  <Typography variant="body2" color="text.secondary">
                    {t('discounts')}
                  </Typography>
                  <Typography
                    variant="body2"
                    color="text.secondary"
                    sx={{ textAlign: 'right', minWidth: '200px' }}
                  >
                    (
                    {new Intl.NumberFormat('en-US', {
                      style: 'currency',
                      currency: savedCart?.currency || 'usd',
                    }).format(
                      savedCart.priceBreakdown.reduce(
                        (sum, item) => sum + item.discount,
                        0
                      ) / 100
                    )}
                    )
                  </Typography>
                </Box>
              )}

              {savedCart.priceBreakdown.some(
                (item) => item.balanceAdjustment !== 0
              ) && (
                <Box
                  display="flex"
                  justifyContent="space-between"
                  alignItems="center"
                >
                  <Typography variant="body2" color="text.secondary">
                    {t('adjustments')}
                  </Typography>
                  <Typography
                    variant="body2"
                    color="text.secondary"
                    sx={{ textAlign: 'right', minWidth: '200px' }}
                  >
                    {savedCart.priceBreakdown.reduce(
                      (sum, item) => sum + item.balanceAdjustment,
                      0
                    ) > 0
                      ? ''
                      : '('}
                    {new Intl.NumberFormat('en-US', {
                      style: 'currency',
                      currency: savedCart?.currency || 'usd',
                    }).format(
                      Math.abs(
                        savedCart.priceBreakdown.reduce(
                          (sum, item) => sum + item.balanceAdjustment,
                          0
                        )
                      ) / 100
                    )}
                    {savedCart.priceBreakdown.reduce(
                      (sum, item) => sum + item.balanceAdjustment,
                      0
                    ) > 0
                      ? ''
                      : ')'}
                  </Typography>
                </Box>
              )}

              {savedCart.priceBreakdown.some(
                (item) => item.taxesBreakdown?.length > 0
              ) ? (
                savedCart.priceBreakdown
                  .flatMap((item) => item.taxesBreakdown || [])
                  .reduce((acc, tax) => {
                    const existing = acc.find((t) => t.label === tax.label);
                    if (existing) {
                      existing.amount += tax.amount;
                    } else {
                      acc.push({ ...tax });
                    }
                    return acc;
                  }, [] as { label: string; amount: number }[])
                  .map((tax, taxIndex) => (
                    <Box
                      key={taxIndex}
                      display="flex"
                      justifyContent="space-between"
                      alignItems="center"
                    >
                      <Typography variant="body2" color="text.secondary">
                        {tax.label}
                      </Typography>
                      <Typography
                        variant="body2"
                        color="text.secondary"
                        sx={{ textAlign: 'right', minWidth: '200px' }}
                      >
                        {new Intl.NumberFormat('en-US', {
                          style: 'currency',
                          currency: savedCart?.currency || 'usd',
                        }).format(tax.amount / 100)}
                      </Typography>
                    </Box>
                  ))
              ) : (
                <Box
                  display="flex"
                  justifyContent="space-between"
                  alignItems="center"
                >
                  <Typography variant="body2" color="text.secondary">
                    {t('taxes')}
                  </Typography>
                  <Typography
                    variant="body2"
                    color="text.secondary"
                    sx={{ textAlign: 'right', minWidth: '200px' }}
                  >
                    {new Intl.NumberFormat('en-US', {
                      style: 'currency',
                      currency: savedCart?.currency || 'usd',
                    }).format(
                      savedCart.priceBreakdown.reduce(
                        (sum, item) => sum + item.tax,
                        0
                      ) / 100
                    )}
                  </Typography>
                </Box>
              )}

              <Divider />

              <Box
                display="flex"
                justifyContent="space-between"
                alignItems="center"
              >
                <Typography variant="subtitle1">{t('total')}</Typography>
                <Typography
                  variant="subtitle1"
                  sx={{ textAlign: 'right', minWidth: '200px' }}
                >
                  {savedCart?.paymentMethodTypeOrId === 'free'
                    ? t('free')
                    : new Intl.NumberFormat('en-US', {
                        style: 'currency',
                        currency: savedCart?.currency || 'usd',
                      }).format(
                        savedCart.priceBreakdown.reduce(
                          (sum, item) => sum + item.total,
                          0
                        ) / 100
                      )}
                </Typography>
              </Box>

              <Box
                display="flex"
                justifyContent="space-between"
                alignItems="center"
              >
                <Typography variant="body2" color="text.secondary">
                  {t('paymentMethod')}
                </Typography>
                <Typography sx={{ textAlign: 'right', minWidth: '200px' }}>
                  {typeof cart.paymentMethod === 'string'
                    ? capitalizeFirstLetter(cart.paymentMethod)
                    : cart.paymentMethod?.brand
                    ? `${
                        capitalizeFirstLetter(
                          cart.paymentMethod?.brand || ''
                        ) || ''
                      } ${cart.paymentMethod?.last4}`
                    : `${capitalizeFirstLetter(
                        cart.paymentMethod?.primary || ''
                      )}`}
                </Typography>
              </Box>
            </Stack>
          </Stack>
        </Card>
      )}

      <Box>
        <Typography variant="subtitle1" align="center" sx={{ mt: 3, mb: 2 }}>
          {t('orderDetails')}
          <Link
            sx={{ ml: 1 }}
            onClick={() => {
              setCurrentStep(PaymentStep.Bag);
              if (isTerminalPaymentMethod(cart)) {
                const terminalPaymentMethod =
                  cart.paymentMethod as PaymentMethod;
                cancelTerminalAction(terminalPaymentMethod.id as string);
              }
            }}
          >
            {t('edit')}
          </Link>
        </Typography>
        <Stack spacing={2}>
          {cart.cartItems.map((item, index) => {
            return (
              <CartItem
                key={item.itemId}
                item={item}
                editable={false}
                index={index}
              />
            );
          })}
        </Stack>
      </Box>
    </Stack>
  );
};

export default PaymentSummary;
